import { useEffect, useMemo, useState } from "react";
// react-joyride is a soft dependency; run `npm install` in ./frontend after
// pulling this change. If the package is missing at build time, the import
// will fail loudly — which is the right behaviour.
import Joyride, { STATUS, type CallBackProps, type Step } from "react-joyride";
import { useDemoSession } from "@/hooks/useDemoSession";
import type { DemoPersona } from "@/api/auth";

const TOURS: Record<DemoPersona, Step[]> = {
  hr_admin: [
    {
      target: 'a[href="/"]',
      content: "Your command center. Key metrics, approvals, and AI agent activity.",
      disableBeacon: true,
    },
    {
      target: 'a[href="/agents"]',
      content: "Meet your AI team — Atlas, Sentinel, and Pilot work while you sleep.",
    },
    {
      target: 'a[href="/employees"]',
      content:
        "Complete employee directory. Import via CSV, manage the full lifecycle.",
    },
    {
      target: 'a[href="/reports"]',
      content:
        "Pre-built and custom reports on headcount, turnover, compensation.",
    },
    {
      target: "[data-demo-banner]",
      content: "Ready to try with your own team? Sign up free.",
    },
  ],
  manager: [
    {
      target: 'a[href="/my/team"]',
      content:
        "Your direct reports at a glance — who's out, pending docs, reviews.",
      disableBeacon: true,
    },
    {
      target: 'a[href="/approvals"]',
      content: "Approve time-off and requests in one click.",
    },
    {
      target: 'a[href="/one-on-ones"]',
      content: "Track talking points and action items for every 1:1.",
    },
    {
      target: "[data-demo-banner]",
      content: "Ready to try with your own team? Sign up free.",
    },
  ],
  employee: [
    {
      target: 'a[href="/my-documents"]',
      content: "Your pending documents and signatures in one place.",
      disableBeacon: true,
    },
    {
      target: 'a[href="/leave"]',
      content: "Request PTO, check your balance, see who's out.",
    },
    {
      target: 'a[href="/directory"]',
      content: "Find and connect with anyone in the company.",
    },
    {
      target: "[data-demo-banner]",
      content: "Like what you see? Sign up free.",
    },
  ],
  executive: [
    {
      target: 'a[href="/agents"]',
      content:
        "AI-generated executive summary — metrics, trends, priorities.",
      disableBeacon: true,
    },
    {
      target: 'a[href="/agents"]',
      content: "Automated compliance monitoring. Zero manual checks.",
    },
    {
      target: 'a[href="/reports"]',
      content:
        "Headcount, turnover, compensation — all automated.",
    },
    {
      target: "[data-demo-banner]",
      content: "Ready to try with your own data? Sign up free.",
    },
  ],
};

const TOUR_SHOWN_KEY = "lenavio.demoTour.shown";
const TOUR_PENDING_KEY = "lenavio.demoTour.pending";

/**
 * Intro modal + Joyride-driven walkthrough for first-time demo visitors.
 * Shown once per browser session per persona, after a 600ms delay so the
 * sidebar has time to mount.
 */
export function DemoTour() {
  const { isDemo, persona } = useDemoSession();
  const [showIntro, setShowIntro] = useState(false);
  const [running, setRunning] = useState(false);

  const steps = useMemo<Step[]>(
    () => (persona ? TOURS[persona] : []),
    [persona]
  );

  useEffect(() => {
    if (!isDemo || !persona) return;
    const pending = sessionStorage.getItem(TOUR_PENDING_KEY);
    const shown = sessionStorage.getItem(TOUR_SHOWN_KEY);
    if (pending === persona && shown !== persona) {
      const t = setTimeout(() => setShowIntro(true), 600);
      return () => clearTimeout(t);
    }
  }, [isDemo, persona]);

  if (!isDemo || !persona) return null;

  const dismiss = () => {
    sessionStorage.setItem(TOUR_SHOWN_KEY, persona);
    sessionStorage.removeItem(TOUR_PENDING_KEY);
    setShowIntro(false);
  };

  const startTour = () => {
    sessionStorage.setItem(TOUR_SHOWN_KEY, persona);
    sessionStorage.removeItem(TOUR_PENDING_KEY);
    setShowIntro(false);
    setRunning(true);
  };

  const onTourCallback = (data: CallBackProps) => {
    const terminal: string[] = [STATUS.FINISHED, STATUS.SKIPPED];
    if (terminal.includes(data.status)) setRunning(false);
  };

  return (
    <>
      {showIntro && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h2 className="text-xl font-semibold text-gray-900">
              Want a quick tour?
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              We&apos;ll show you the highlights in 60 seconds.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={dismiss}
                className="rounded-md px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
              >
                I&apos;ll explore on my own
              </button>
              <button
                type="button"
                onClick={startTour}
                className="rounded-md px-4 py-2 text-sm font-semibold text-white"
                style={{ backgroundColor: "#1B18FF" }}
              >
                Show me around
              </button>
            </div>
          </div>
        </div>
      )}

      <Joyride
        steps={steps}
        run={running}
        continuous
        showSkipButton
        callback={onTourCallback}
        styles={{
          options: {
            primaryColor: "#1B18FF",
            zIndex: 10000,
          },
        }}
      />
    </>
  );
}
